/**
 * Render anchor — the float32 half of the FP64 origin rebase (2026-08-23).
 *
 * AtlasOriginRebaseModule holds `scene.position` at minus-the-current-origin so
 * an ordinary child's huge translation cancels in f64 on the CPU before the
 * f32 GPU upload. INSTANCE matrices never get that treatment: InstancedMesh /
 * BatchedMesh keep them in a float32 attribute and the vertex shader adds the
 * ~7e5 m translation to each vertex BEFORE anything cancels — at 720 km the
 * float32 ulp is 6 cm, so every vertex snaps to a 6 cm grid. That was the
 * "versonas/props look shattered up close, fine at a distance, structures
 * fine" report: big structures are plain meshes (CPU path), the small things
 * are instanced.
 *
 * Every instanced pool therefore parents its meshes under an ANCHOR GROUP
 * positioned at the current origin and stores matrices RELATIVE to it
 * (offsets ≤ the 10 km rebase threshold → ≤ 1 mm ulp). The anchor only moves
 * when the rebase module moves the origin; subscribers rewrite their stored
 * matrices then (rare: once per 10 km travelled).
 */
import * as THREE from 'three/webgpu';

let anchorX = 0;
let anchorZ = 0;
const listeners = new Set<(x: number, z: number) => void>();

/** The current render anchor (absolute world metres, integer). */
export function renderAnchor(): { x: number; z: number } { return { x: anchorX, z: anchorZ }; }
export function renderAnchorX(): number { return anchorX; }
export function renderAnchorZ(): number { return anchorZ; }

/** Move the anchor (the rebase module is the only intended writer). No-op when unchanged. */
export function setRenderAnchor(x: number, z: number): void {
  if (x === anchorX && z === anchorZ) return;
  anchorX = x; anchorZ = z;
  for (const cb of [...listeners]) cb(x, z);
}

/** Subscribe to anchor moves; returns the unsubscribe. */
export function onRenderAnchor(cb: (x: number, z: number) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** `out` = `m` with the anchor subtracted from its translation (anchor-relative copy). */
export function toAnchorLocal(m: THREE.Matrix4, out: THREE.Matrix4): THREE.Matrix4 {
  out.copy(m);
  out.elements[12] -= anchorX;
  out.elements[14] -= anchorZ;
  return out;
}

/**
 * A group parked at the anchor (absolute coords — the scene rebase then
 * makes its matrixWorld small). Children must carry ANCHOR-RELATIVE transforms.
 */
export function makeAnchorGroup(name: string): { group: THREE.Group; release: () => void } {
  const group = new THREE.Group();
  group.name = name;
  group.position.set(anchorX, 0, anchorZ);
  const off = onRenderAnchor((x, z) => { group.position.set(x, 0, z); group.updateMatrixWorld(true); });
  return { group, release: off };
}

/** Test hook: reset the anchor + listeners (harnesses only). */
export function __resetRenderAnchor(): void { anchorX = 0; anchorZ = 0; listeners.clear(); }

/**
 * THE SCENE SHIFT, for the two three.js reads that take WORLD space while a scene child is written in its PARENT's
 * frame (bug scan 2026-10-08). The rebase holds `scene.position` at −origin, so a child's world position is its own
 * position PLUS that shift — hundreds of km in the Commons, up to 2,400 km in the sector.
 *
 * `Object3D.lookAt(target)` aims from the object's WORLD position at the target AS GIVEN: a target in the parent's
 * frame turned every hull, light and silhouette toward −shift instead of its heading. `localTarget` is overwritten.
 */
export function lookAtLocal(o: THREE.Object3D, localTarget: THREE.Vector3): void {
  o.lookAt(o.parent ? o.parent.localToWorld(localTarget) : localTarget);
}

/**
 * three's HemisphereLightNode takes "up" as normalize(the light's WORLD position): a light left at its default
 * (0, 1, 0) under a shifted scene took its sky colour from the horizon. Seats the light so that its world position is
 * the direction given (default straight up); call again whenever the shift may have moved.
 */
export function seatHemisphereUp(light: THREE.Object3D, x = 0, y = 1, z = 0): void {
  light.position.set(x, y, z);
  if (light.parent) light.parent.worldToLocal(light.position);
}

/**
 * THE INSTANCE PRECISION LAW for a static batch (bug sweep 2026-09-22). Settlement and hinterland
 * dressing wrote ABSOLUTE instance matrices (hundreds of km out) into float32 — 3–6 cm vertex
 * snapping on walls, fences, wells and the hinterland trees at every distant site (the header's
 * "shattered" look). The mesh is seated at the batch's rounded XZ centroid (a CPU f64 transform the
 * scene rebase cancels) and each instance is stored relative to it — a site's spread keeps float32
 * error under a millimetre. `post` is multiplied onto each matrix first (a GLB part's local).
 * World = parent × T(c) × (M·post − c) = parent × M·post, whatever the parent.
 */
export function writeInstancesNearCentroid(im: THREE.InstancedMesh, mats: readonly THREE.Matrix4[], post?: THREE.Matrix4): void {
  let sx = 0, sz = 0;
  for (const m of mats) { sx += m.elements[12]; sz += m.elements[14]; }
  const cx = mats.length ? Math.round(sx / mats.length) : 0;
  const cz = mats.length ? Math.round(sz / mats.length) : 0;
  im.position.set(cx, 0, cz);
  im.updateMatrix();
  const tmp = new THREE.Matrix4();
  for (let i = 0; i < mats.length; i++) {
    tmp.copy(mats[i]);
    if (post) tmp.multiply(post);
    tmp.elements[12] -= cx;
    tmp.elements[14] -= cz;
    im.setMatrixAt(i, tmp);
  }
  im.instanceMatrix.needsUpdate = true;
}
